import { personalId as personalIdValidator } from '@klarna/mf-validations';
import { PERSONAL_ID_DOCUMENT_TYPE } from '@/constants';

const FIELDS = {
  US: {
    isRequired: true,
    formatValidator: value =>
      personalIdValidator.validate(value, {
        country: 'US',
        documentType: PERSONAL_ID_DOCUMENT_TYPE.SOCIAL_SECURITY_NUMBER_MASKED,
      }),
    prefix: '*** - ** - ',
    mask: '*** - ** - ****',
    translationKey: 'US',
    canDeriveDateOfBirth: false,
  },
  US_FULL_SSN: {
    isRequired: true,
    formatValidator: value =>
      personalIdValidator.validate(value, {
        country: 'US',
        documentType: PERSONAL_ID_DOCUMENT_TYPE.SOCIAL_SECURITY_NUMBER,
      }),
    translationKey: 'US_FULL_SSN',
    canDeriveDateOfBirth: false,
  },
  CA: {}, // For CA we do not collect personal ID, no matter which country
  SE: {
    isRequired: true,
    formatValidator: value =>
      personalIdValidator.validate(value, {
        country: 'SE',
        documentType: PERSONAL_ID_DOCUMENT_TYPE.PERSONAL_IDENTIFICATION_NUMBER,
      }),
    translationKey: 'SE',
    canDeriveDateOfBirth: true,
  },
  FI: {
    isRequired: true,
    formatValidator: value =>
      personalIdValidator.validate(value, {
        country: 'FI',
        documentType: PERSONAL_ID_DOCUMENT_TYPE.PERSONAL_IDENTIFICATION_CODE,
      }),
    translationKey: 'FI',
    canDeriveDateOfBirth: true,
  },
  NO: {
    isRequired: true,
    formatValidator: value =>
      personalIdValidator.validate(value, {
        country: 'NO',
        documentType: PERSONAL_ID_DOCUMENT_TYPE.BIRTH_NUMBER,
      }),
    translationKey: 'NO',
    canDeriveDateOfBirth: true,
  },
  DK: {
    isRequired: true,
    formatValidator: value =>
      personalIdValidator.validate(value, {
        country: 'DK',
        documentType: PERSONAL_ID_DOCUMENT_TYPE.PERSONAL_IDENTIFICATION_NUMBER,
      }),
    translationKey: 'DK',
    canDeriveDateOfBirth: true,
  },
  IT: {
    isRequired: true,
    formatValidator: value =>
      personalIdValidator.validate(value, {
        country: 'IT',
        documentType: PERSONAL_ID_DOCUMENT_TYPE.PERSONAL_IDENTIFICATION_NUMBER,
      }),
    translationKey: 'IT',
    canDeriveDateOfBirth: false,
  },
  ES: {
    isRequired: true,
    formatValidator: value =>
      personalIdValidator.validate(value, {
        country: 'ES',
        documentType: PERSONAL_ID_DOCUMENT_TYPE.PERSONAL_IDENTIFICATION_NUMBER,
      }),
    translationKey: 'ES',
    canDeriveDateOfBirth: false,
  },
  PL: {
    isRequired: true,
    formatValidator: value =>
      personalIdValidator.validate(value, {
        country: 'PL',
        documentType: PERSONAL_ID_DOCUMENT_TYPE.PERSONAL_IDENTIFICATION_NUMBER,
      }),
    translationKey: 'PL',
    canDeriveDateOfBirth: true,
  },
  DEFAULT: {
    isRequired: true,
    formatValidator: () => true,
    translationKey: 'default',
    canDeriveDateOfBirth: false,
  },
};

export const getField = (
  region,
  merchantCountry,
  stakeholderCountry,
  merchantEntityType = null,
) => {
  // Better be safe
  const uRegion = region.toUpperCase();
  // const uMerchantCountry = merchantCountry.toUpperCase();

  // Stakeholder country defaults to merchant country when null
  const uStakeholderCountry = (stakeholderCountry || merchantCountry).toUpperCase();

  // export this later if necessary
  const PNO_COUNTRIES = ['FI', 'SE', 'DK', 'NO', 'IT', 'ES', 'PL'];

  // Special case for US Sole Traders
  if (
    merchantCountry === 'US' &&
    uStakeholderCountry === 'US' &&
    merchantEntityType === 'SOLE_PROPRIETORSHIP'
  ) {
    return FIELDS.US_FULL_SSN;
  }

  // For US merchant, always collect last 4 digits of SSN
  if (uRegion === 'US') {
    if (['US', 'CA'].includes(uStakeholderCountry)) {
      return FIELDS[uStakeholderCountry];
    } else {
      return FIELDS.DEFAULT;
    }
  } else if (stakeholderCountry === 'US') {
    return FIELDS.US;
  } else if (uRegion === 'EU') {
    if (PNO_COUNTRIES.includes(uStakeholderCountry)) {
      return FIELDS[uStakeholderCountry];
    }
  }

  // We are mostly using this with desctructuring, so better to return empty object here.
  return {};
};

export const isRequired = ({ region, merchantCountry, stakeholderCountry }) => {
  const { isRequired } = getField(region, merchantCountry, stakeholderCountry);
  return isRequired;
};
